/**
 * The operator ledger's preset views and filter set (design D3).
 *
 * Every preset is a **server-side** predicate: the console sends `view=` and
 * renders the page and total the server returned. Filtering client-side would
 * filter only the current page and report a subset as though it were the whole
 * set.
 */

export type PresetView = 'all' | 'topup' | 'deduction' | 'bonus'

export const PRESET_VIEWS: ReadonlyArray<{ id: PresetView; label: string }> = [
  { id: 'all', label: '全部' },
  { id: 'topup', label: '充值' },
  { id: 'deduction', label: '消费' },
  { id: 'bonus', label: '赠送与回收' },
]

export const ENTRY_TYPES = [
  'topup', 'deduction', 'adjustment', 'refund', 'clawback', 'expiry',
] as const

export const DIRECTIONS = ['credit', 'debit'] as const

/** The `view` query parameter for a preset — `null` for "all entries". */
export function viewParam(preset: PresetView): string | null {
  return preset === 'all' ? null : preset
}

export interface LedgerFilterInput {
  entryType: string
  direction: string
  createdAfter: string
  createdBefore: string
}

/**
 * Build the ledger request's filter set. An empty control contributes no
 * parameter at all — the server treats absent and empty identically, and
 * sending `entry_type=` would be an empty spelling of "no filter" that the
 * response-matching context would then have to treat as distinct.
 */
export function ledgerFilterParams(input: LedgerFilterInput): Record<string, string> {
  const params: Record<string, string> = {}
  if (input.entryType) params.entry_type = input.entryType
  if (input.direction) params.direction = input.direction
  if (input.createdAfter) params.created_after = input.createdAfter
  if (input.createdBefore) params.created_before = input.createdBefore
  return params
}

export interface ListFilterInput {
  externalRef: string
  userId: string
  hasBalance: string
}

/** The account list's filter set, under the same absent-vs-empty rule. */
export function listFilterParams(input: ListFilterInput): Record<string, string> {
  const params: Record<string, string> = {}
  if (input.externalRef.trim()) params.external_ref = input.externalRef.trim()
  if (input.userId.trim()) params.user_id = input.userId.trim()
  if (input.hasBalance) params.has_balance = input.hasBalance
  return params
}
